/**
 * ControlPanel Component
 * Main control interface for simulation parameters
 */

import React, { useState, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import { Space, Input, Select, Tooltip } from "antd";
import { SettingOutlined, QuestionCircleOutlined, HomeOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { CITY_TEMPERATURES } from "./constants";
import { clamp, calculateRadiatorHeatTransfer, calculateVolumetricFlow, calculateEnvelopeLoss, calculateVentilationLoss, calculateRoomVolume } from "./utils";
import { AutoAdjustSupplyButton } from "./HeatingCurve";
import styles from "./HeatPumpSimulation.module.css";
import { LatexRenderer } from "../../Shared/LatexRenderer";

// Import radiator type images
import radiatorType10 from "../../Shared/Type_10.png";
import radiatorType11 from "../../Shared/Type_11.png";
import radiatorType21 from "../../Shared/Type_21.png";
import radiatorType22 from "../../Shared/Type_22.png";
import radiatorType33 from "../../Shared/Type_33.png";

// Detect if user is on a mobile/touch device
const isMobileDevice = () => {
  return (
    'ontouchstart' in window ||
    navigator.maxTouchPoints > 0 ||
    /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  );
};

/**
 * NumericInput - A controlled input with mobile-friendly debounce behavior
 * - On mobile: Uses local state and commits on blur OR after typing delay (prevents min/max issues)
 * - On desktop: Commits immediately for instant feedback
 * @param {number} value - The current value
 * @param {function} onChange - Called with validated value
 * @param {function} onCommit - Optional callback called after onChange with the validated value
 * @param {number} min - Minimum allowed value
 * @param {number} max - Maximum allowed value
 * @param {number} defaultValue - Fallback value if input is invalid
 * @param {number} debounceMs - Milliseconds to wait after typing before committing on mobile (default: 1400)
 */
const NumericInput = ({ value, onChange, onCommit, min, max, defaultValue, debounceMs = 1400, ...props }) => {
  const [localValue, setLocalValue] = useState(value);
  const [isFocused, setIsFocused] = useState(false);
  const debounceTimerRef = useRef(null);
  const isMobile = useRef(isMobileDevice()).current;

  // Sync local value with external value when not focused (mobile) or always (desktop)
  useEffect(() => {
    if (!isMobile || !isFocused) {
      setLocalValue(value);
    }
  }, [value, isFocused, isMobile]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  const commitValue = (val) => {
    const parsed = parseFloat(val);
    let finalValue;
    if (isNaN(parsed)) {
      finalValue = defaultValue !== undefined ? defaultValue : min;
    } else {
      finalValue = clamp(parsed, min, max);
    }
    setLocalValue(finalValue);
    onChange(finalValue);
    if (onCommit) {
      onCommit(finalValue);
    }
  };

  const handleChange = (e) => {
    const newValue = e.target.value;
    setLocalValue(newValue);

    // Clear existing timer
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    if (isMobile) {
      // Mobile: debounce the commit
      debounceTimerRef.current = setTimeout(() => {
        commitValue(newValue);
      }, debounceMs);
    } else {
      // Desktop: commit immediately (still validate)
      const parsed = parseFloat(newValue);
      if (!isNaN(parsed)) {
        const clamped = clamp(parsed, min, max);
        onChange(clamped);
        if (onCommit) {
          onCommit(clamped);
        }
      }
    }
  };

  const handleFocus = () => {
    setIsFocused(true);
  };

  const handleBlur = () => {
    setIsFocused(false);
    // Clear debounce timer since we're committing immediately
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    commitValue(localValue);
  };

  return (
    <Input
      type="number"
      min={min}
      max={max}
      value={localValue}
      onChange={handleChange}
      onFocus={handleFocus}
      onBlur={handleBlur}
      {...props}
    />
  );
};

// Radiator type configurations
const RADIATOR_TYPES = [
  { type: "10", label: "10", image: radiatorType10 },
  { type: "11", label: "11", image: radiatorType11 },
  { type: "21", label: "21", image: radiatorType21 },
  { type: "22", label: "22", image: radiatorType22 },
  { type: "33", label: "33", image: radiatorType33 }
];

const CitySelector = ({ value, onChange, onTemperatureChange, disabled = false }) => {
  const { t } = useTranslation();
  const cities = Object.entries(CITY_TEMPERATURES);

  return (
    <Select
      optionLabelProp="label"
      placeholder={
      <Space direction="vertical" size="small" align="start">
        <small className="default-gray">{t('heatPumpSimulation.outdoor.designTemperature')}</small>
        <small className="default-gray">{t('heatPumpSimulation.outdoor.chooseCity')}</small>
      </Space>}
      value={value}
      style={{ width: 200 }}
      disabled={disabled}
      onChange={(cityKey) => {
        const city = CITY_TEMPERATURES[cityKey];
        if (city) {
          onTemperatureChange(city.temperature);
          onChange(cityKey);
        }
      }}
    >
      {cities.map(([key, city]) => (
        <Select.Option
          key={key}
          value={key}
          label={(
            <Space direction="vertical" size="small" align="start">
              <small className="default-gray">{t('heatPumpSimulation.outdoor.designTemperature')}</small>
              <div className="default-opposite-arrangement" style={{ width: 125 }} >
                <span className="default-gray">{city.name}</span>
                <span className="default-red">{city.temperature} °C</span>
              </div>
            </Space>
          )}
        >
          <Space className="default-opposite-arrangement">
            <span className="default-gray">{city.name}</span>
            <span className="default-red">{city.temperature} °C</span>
          </Space>
        </Select.Option>
      ))}
    </Select>
  );
};

const RoomTypeSelector = ({ value, onChange, onTemperatureChange, disabled = false }) => {
  const { t } = useTranslation();
  const roomTypes = [
    { key: "living", name: t('heatPumpSimulation.room.living'), temperature: 20 },
    { key: "bathroom", name: t('heatPumpSimulation.room.bathroom'), temperature: 24 },
  ];

  return (
    <Select
      optionLabelProp="label"
      value={value ?? undefined}
      placeholder={t('heatPumpSimulation.room.chooseType', 'Choose room type')}
      style={{ width: 195 }}
      disabled={disabled}
      onChange={(roomKey) => {
        const room = roomTypes.find((r) => r.key === roomKey);
        if (room) {
          onTemperatureChange(room.temperature);
          onChange(roomKey);
        }
      }}
    >
      {roomTypes.map((room) => (
        <Select.Option
          key={room.key}
          value={room.key}
          label={(
            <div className="default-opposite-arrangement" style={{ width: 145 }} >
              <span className="default-gray">{room.name}</span>
              <span className="default-red">{room.temperature} °C</span>
            </div>
          )}
        >
          <Space className="default-opposite-arrangement">
            <span className="default-gray">{room.name}</span>
            <span className="default-red">{room.temperature} °C</span>
          </Space>
        </Select.Option>
      ))}
    </Select>
  );
};

export const ControlPanel = ({
  // Temperature parameters
  outsideTemperature,
  onOutsideTemperatureChange,
  targetTemperature,
  onTargetTemperatureChange,
  supplyTemperature,
  onSupplyTemperatureChange,

  // Building parameters
  uValue,
  onUValueChange,
  totalArea,
  onTotalAreaChange,
  rValue,
  roofArea,
  wallArea,

  // Radiator parameters
  radiatorType,
  onRadiatorTypeChange,
  radiatorTypeLocked = false,
  radiatorSize,
  onRadiatorSizeChange,
  radiatorSizeLocked = false,
  nominalPower,
  valvePosition,

  // System parameters
  systemPressure,
  onSystemPressureChange,
  airChangeRate,
  onAirChangeRateChange,

  // UI handlers
  onShowUParametersClick,
  onShowRadiatorTypesClick,
  onShowBuildingEraClick,
  onShowMapLocationClick,
  onShowHeatingCurveClick,

  // Weather compensation
  autoWeatherCompensation,
  onAutoWeatherCompensationChange,
  currentRoomTemperature,

  // Selected values
  selectedCity,
  onSelectedCityChange,
  selectedRoomType,
  onSelectedRoomTypeChange,
  profileControlState = {},
}) => {
  const { t } = useTranslation();
  const isLocked = profileControlState.isLocked || (() => false);
  const isHidden = profileControlState.isHidden || (() => false);
  const isControlHidden = (...keys) => keys.some((key) => isHidden(key) || isHidden(`controlPanel.${key}`));
  const showAutoWeatherToggle = !isControlHidden('autoWeatherCompensation');
  const showHeatingCurveButton = !isControlHidden('heatingCurveButton');
  const showOutdoorTemperatureInput = !isControlHidden('outdoorTemperature');
  const showOutdoorMapButton = !isControlHidden('mapSelectorButton');
  const showOutdoorCitySelector = !isControlHidden('selectedCity');
  const showOutdoorClimateLink = !isControlHidden('climateMapLink');
  const showOutdoorPanel = showOutdoorTemperatureInput || showOutdoorMapButton || showOutdoorCitySelector || showOutdoorClimateLink;
  const showBuildingUValue = !isControlHidden('uValue', 'envelopeUValue');
  const showBuildingTotalArea = !isControlHidden('totalArea');
  const showBuildingParametersButton = !isControlHidden('buildingParametersButton');
  const showBuildingEraButton = !isControlHidden('buildingEraButton');
  const showBuildingCoreSection = showBuildingUValue || showBuildingTotalArea || showBuildingParametersButton || showBuildingEraButton;
  const showAirChangeRateSection = !isControlHidden('airChangeRate');
  const showRadiatorTypeControl = !isControlHidden('radiatorType');
  const showRadiatorSizeControl = !isControlHidden('radiatorCount', 'radiatorSize');
  const showRadiatorGuideButton = !isControlHidden('radiatorGuideButton');
  const showRadiatorSection = showRadiatorTypeControl || showRadiatorSizeControl || showRadiatorGuideButton;
  const showBuildingPanel = showBuildingCoreSection || showAirChangeRateSection || showRadiatorSection;
  const showRoomTemperatureControl = !isControlHidden('desiredTemperature');
  const showRoomTypeControl = !isControlHidden('selectedRoomType');
  const showRoomSection = showRoomTemperatureControl || showRoomTypeControl;
  const showSupplyTemperatureControl = !isControlHidden('supplyTemperature');
  const showSupplySection = showSupplyTemperatureControl || showAutoWeatherToggle || showHeatingCurveButton;
  const showSystemPressureSection = !isControlHidden('systemPressure');
  const showRoomSystemPanel = showRoomSection || showSupplySection || showSystemPressureSection;
  const showAnyPanel = showOutdoorPanel || showBuildingPanel || showRoomSystemPanel;

  // Calculate design power at current supply temperature
  const radiatorCount = radiatorSize / 10;
  const volumetricFlow = calculateVolumetricFlow(systemPressure, 5, 15); // Using default pipe params
  const designPowerResult = calculateRadiatorHeatTransfer({
    supplyTemperature,
    roomTemperature: targetTemperature,
    volumetricFlow,
    valvePosition,
    thermostatOpening: 1,
    radiatorCount,
    radiatorType,
  });
  const designPower = (designPowerResult?.heatOutput || 0) / 1000; // Convert W to kW

  // Calculate losses at target temperature (not current room temperature)
  const envelopeLossAtTarget = calculateEnvelopeLoss(uValue, totalArea, targetTemperature, outsideTemperature, roofArea);
  const roomVolume = calculateRoomVolume(roofArea, wallArea);
  const ventilationLossAtTarget = calculateVentilationLoss(targetTemperature, outsideTemperature, roomVolume, airChangeRate);

  if (!showAnyPanel) {
    return null;
  }

  return (
    <Space direction="vertical" size="middle" align="start" className="control-panel" data-testid="control-panel">
      {/* Outdoor Temperature Panel */}
      {showOutdoorPanel && <div className={styles.parametersPanel} data-testid="control-panel-outdoor-section">
          <Space direction="vertical" align="start">
            <small className="default-title">{t('heatPumpSimulation.outdoor.title')}</small>
            <Space size="middle" data-testid="control-panel-outdoor-controls">
              {showOutdoorTemperatureInput && (
                <div data-testid="outdoor-temperature-input">
                  <NumericInput
                    min={-10}
                    max={20}
                    defaultValue={-10}
                    value={outsideTemperature}
                    style={{ width: 102}}
                    suffix="°C"
                    disabled={isLocked('outdoorTemperature')}
                    onChange={onOutsideTemperatureChange}
                    onCommit={() => onSelectedCityChange(null)}
                  />
                </div>
              )}
              {showOutdoorMapButton && (
                <Tooltip title={t('heatPumpSimulation.outdoor.selectFromMap')} color="white">
                  <span data-testid="map-selector-button">
                    <EnvironmentOutlined
                      className={`default-gray ${isLocked('outdoorTemperature') ? '' : 'hq-clickable'}`}
                      style={{ fontSize: '18px', opacity: isLocked('outdoorTemperature') ? 0.45 : 1 }}
                      onClick={isLocked('outdoorTemperature') ? undefined : onShowMapLocationClick}
                    />
                  </span>
                </Tooltip>
              )}
              {showOutdoorCitySelector && (
                <div data-testid="control-panel-city-selector">
                  <CitySelector
                    value={selectedCity}
                    onChange={onSelectedCityChange}
                    onTemperatureChange={onOutsideTemperatureChange}
                    disabled={isLocked('outdoorTemperature') || isLocked('selectedCity')}
                  />
                </div>
              )}
              {showOutdoorClimateLink && (
                <Tooltip title={t('heatPumpSimulation.outdoor.mapLink')} color="white" placement="right">
                  <span data-testid="climate-map-link">
                    <QuestionCircleOutlined
                      className="default-gray hq-clickable"
                      onClick={() => window.open("https://www.waermepumpe.de/werkzeuge/klimakarte/")}
                    />
                  </span>
                </Tooltip>
              )}
            </Space>
        </Space>
      </div>}

      {/* Building Thermal Properties Panel */}
      {showBuildingPanel && <Space className={styles.parametersPanel} direction="vertical" size="small" align="start" data-testid="control-panel-building-section">
        {showBuildingCoreSection && <Space direction="vertical" align="start" size="small" data-testid="control-panel-building-core">
          <small className="default-title">{t('heatPumpSimulation.building.title')}</small>
          <Space align="center" size="large" data-testid="control-panel-building-core-controls">
            {showBuildingUValue && (
              <div data-testid="u-value-control">
                <NumericInput
                  min={0.1}
                  max={5}
                  step={0.05}
                  defaultValue={0.1}
                  value={uValue}
                  style={{ width: 160, textAlign: "center" }}
                  disabled={isLocked('uValue') || isLocked('envelopeUValue')}
                  prefix={
                    <Tooltip color="white" title={<p>{t('heatPumpSimulation.building.averageUValue')}</p>}>
                      <div style={{ marginRight: 15 }}>
                        <LatexRenderer latex="$ \overline{U} $" />
                      </div>
                    </Tooltip>
                  }
                  suffix={<LatexRenderer latex="$ \frac{W}{K \cdot m^2 } $" />}
                  onChange={onUValueChange}
                />
              </div>
            )}

            {showBuildingTotalArea && (
              <div data-testid="total-area-control">
                <NumericInput
                  min={1}
                  max={1000}
                  step={1}
                  defaultValue={1}
                  value={totalArea}
                  style={{ width: 160, textAlign: "center" }}
                  disabled={isLocked('totalArea')}
                  prefix={
                    <Tooltip color="white" title={<p>{t('heatPumpSimulation.building.totalArea')}</p>}>
                      <div style={{ marginRight: 15 }}>
                        <LatexRenderer latex="$ A $" />
                      </div>
                    </Tooltip>
                  }
                  suffix={<LatexRenderer latex="$ m^2 $" />}
                  onChange={onTotalAreaChange}
                />
              </div>
            )}

            <Space direction="vertical" size={6}>
              {showBuildingParametersButton && (
                <span data-testid="u-parameters-btn">
                  <SettingOutlined
                    className="default-gray hq-clickable"
                    title={t('heatPumpSimulation.building.specifySubParams')}
                    onClick={isLocked('uValue') || isLocked('envelopeUValue') ? undefined : onShowUParametersClick}
                  />
                </span>
              )}

              {showBuildingEraButton && (
                <span data-testid="building-era-btn">
                  <HomeOutlined
                    className="default-gray hq-clickable"
                    title={t('heatPumpSimulation.building.selectBuildingEra')}
                    onClick={isLocked('uValue') || isLocked('envelopeUValue') ? undefined : onShowBuildingEraClick}
                  />
                </span>
              )}
            </Space>
          </Space>

          <div className={styles.metricSummaryRow} data-testid="control-panel-building-envelope-metrics">
            <Tooltip
              color="white"
              title={
                <Space direction="vertical" align="start">
                  <LatexRenderer latex="$\color{black}{R = 1 / (A \cdot \overline{U}) } $" />
                  <LatexRenderer latex="$\color{black}{\dot{Q}_\text{loss} = \overline{U} \cdot A \cdot (T_\text{room} - T_\text{outdoor})} $" />
                  <LatexRenderer latex="$\color{black}{\dot{Q}_\text{loss} = \large{\frac{T_\text{room} - T_\text{outdoor}}{R}}}$" />
                </Space>
              }
            >
              <div className={`${styles.metricStack} ${styles.metricStackLeft}`} data-testid="r-value-display">
                <p className={`default-gray ${styles.compactLabel}`}>{t('heatPumpSimulation.building.rValue')}</p>
                <div className={styles.metricValueRow}>
                  <LatexRenderer latex={`$\\small{${(rValue || 0).toFixed(5)}}$`} />
                  <LatexRenderer latex={"$\\small{\\frac{K}{W}}$"} />
                </div>
              </div>
            </Tooltip>

            <div className={`${styles.metricStack} ${styles.metricStackRight}`} data-testid="q-loss-display">
              <p className={`default-gray ${styles.compactLabel}`}>{t('heatPumpSimulation.building.qLoss')} ({targetTemperature.toFixed(1)} °C)</p>
              <div className={`${styles.metricValueRow} ${styles.metricValueRowRight}`}>
                <LatexRenderer latex={`$\\small{${(envelopeLossAtTarget.total / 1000).toFixed(2)}}$`} />
                <LatexRenderer latex={"$\\small{kW}$"} />
              </div>
            </div>
          </div>
        </Space>}
        {/* Air change rate */}
        {showAirChangeRateSection && <Space direction="vertical" align="start" data-testid="control-panel-ventilation-section">
          <small className="default-title">{t('heatPumpSimulation.ventilation.title')}</small>
          <Tooltip color="white" placement="right" title={<p><strong>{t('heatPumpSimulation.ventilation.title')}</strong> <br/> {t('heatPumpSimulation.ventilation.achDescription')} </p>}>
            <div className={styles.metricSummaryRow} data-testid="control-panel-ventilation-combined">
              <div data-testid="air-change-slider" className="air-change-control">
                <NumericInput
                  min={0.25}
                  max={10}
                  step={0.25}
                  defaultValue={0.5}
                  value={airChangeRate}
                  style={{ width: 160 }}
                  suffix="ACH"
                  disabled={isLocked('airChangeRate')}
                  onChange={onAirChangeRateChange}
                />
              </div>
              <div data-testid="ventilation-loss-display" className={`ventilation-loss-display ${styles.metricStack} ${styles.metricStackRight}`}>
                <p className={`default-gray ${styles.compactLabel}`}>{t('heatPumpSimulation.ventilation.qVent')} ({targetTemperature.toFixed(1)} °C)</p>
                <div className={`${styles.metricValueRow} ${styles.metricValueRowRight}`}>
                  <LatexRenderer latex={`$\\small{${(ventilationLossAtTarget / 1000).toFixed(2)}}$`} />
                  <LatexRenderer latex={"$\\small{kW}$"} />
                </div>
              </div>
            </div>
          </Tooltip>
        </Space>} 
        {/* Radiator Type/Size Section */}
        {showRadiatorSection && <Space direction="vertical" align="start" size="small" data-testid="radiator-controls">
          <small className="default-title">{t('heatPumpSimulation.radiator.title')}</small>
          <Space align="center" size="large" data-testid="control-panel-radiator-inputs">
            {showRadiatorTypeControl && (
              <div data-testid="radiator-type-selector">
                <Select
                  value={radiatorType}
                  style={{ width: 120 }}
                  disabled={radiatorTypeLocked}
                  onChange={onRadiatorTypeChange}
                  optionLabelProp="label"
                >
                  {RADIATOR_TYPES.map((radiator) => (
                    <Select.Option 
                      key={radiator.type} 
                      value={radiator.type}
                      label={
                        <Space size="small" align="center">
                          <img src={radiator.image} alt={radiator.type} style={{ height: '35px', width: 'auto' }} />
                          <span>{radiator.type}</span>
                        </Space>
                      }
                    >
                      <Space size="small" align="center">
                        <img src={radiator.image} alt={radiator.type} style={{ height: '35px', width: 'auto' }} />
                        <span>{radiator.label}</span>
                      </Space>
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {showRadiatorSizeControl && (
              <div data-testid="radiator-size">
                <Select
                  value={radiatorSize}
                  style={{ width: 200 }}
                  disabled={radiatorSizeLocked}
                  onChange={onRadiatorSizeChange}
                >
                  {[40, 50, 60, 70, 80, 90, 100].map((size) => (
                    <Select.Option key={size} value={size}>
                      {'L '} {size} cm {', '} {'H 60 cm'}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}

            {showRadiatorGuideButton && (
              <span data-testid="radiator-guide-button">
                <QuestionCircleOutlined
                  className="default-gray hq-clickable"
                  title={t('heatPumpSimulation.radiator.types')}
                  onClick={onShowRadiatorTypesClick}
                />
              </span>
            )}
          </Space>

          <div className={styles.metricSummaryRow} data-testid="heat-output-display">
            <Tooltip color="white" placement="left" title={<p> <strong>P<sub>nominal</sub><br/></strong> {t('heatPumpSimulation.radiator.nominalPower')}</p>}>
              <div className={`${styles.metricStack} ${styles.metricStackLeft}`} data-testid="radiator-nominal-power-display">
                <p className={`default-gray ${styles.compactLabel}`}>P nominal</p>
                <div className={styles.metricValueRow}>
                  <LatexRenderer latex={`$\\small{${nominalPower.toFixed(2)}}$`} />
                  <LatexRenderer latex={"$\\small{kW}$"} />
                </div>
              </div>
            </Tooltip>
            
            <div className={`${styles.metricStack} ${styles.metricStackRight}`} data-testid="radiator-design-power-display">
              <p className={`default-gray ${styles.compactLabel}`}>{t('heatPumpSimulation.radiator.pDesign')} ({supplyTemperature} °C/{supplyTemperature-5} °C)</p>
              <div className={`${styles.metricValueRow} ${styles.metricValueRowRight}`}>
                <LatexRenderer latex={`$\\small{${designPower.toFixed(2)}}$`} />
                <LatexRenderer latex={"$\\small{kW}$"} />
              </div>
            </div>
          </div>
          <div style={{ borderTop: "1px solid #cccccc", paddingTop: '4px', marginTop: '2px' }} className={styles.metricSummaryRow} data-testid="q-net-display">
            <div data-testid="radiator-q-net-display" className={`${styles.metricValueRow} ${styles.metricValueRowRight}`} style={{ width: '100%' }}>
              <p className={`default-gray ${styles.compactLabel}`}>{t('heatPumpSimulation.radiator.qNet')}</p>
              <span style={{ color: (designPower * 1000 - envelopeLossAtTarget.total - ventilationLossAtTarget) < 0 ? '#ff4d4f' : 'inherit' }}>
                <LatexRenderer latex={`$\\small{${((designPower * 1000 - envelopeLossAtTarget.total - ventilationLossAtTarget) / 1000).toFixed(2)}}$`} />
              </span>
              <LatexRenderer latex={"$\\small{kW}$"} />
            </div>
          </div>
        </Space>}
      </Space>}

      {/* Room Temperature and System Parameters Panel */}
      {showRoomSystemPanel && <Space className={styles.parametersPanel} direction="vertical" size="small" align="start" data-testid="control-panel-room-system-section">
        {showRoomSection && <Space direction="vertical" align="start" data-testid="room-settings-group">
          <small className="default-title">{t('heatPumpSimulation.room.title')}</small>
          <Space size="large" data-testid="control-panel-room-controls">
            {showRoomTemperatureControl && (
              <div data-testid="desired-temperature-control">
                <NumericInput
                  min={10}
                  max={30}
                  defaultValue={20}
                  value={targetTemperature}
                  style={{ width: 160 }}
                  suffix="°C"
                  disabled={isLocked('desiredTemperature')}
                  onChange={onTargetTemperatureChange}
                  onCommit={(val) => {
                    // Auto-select room type if it matches
                    if (val === 20) onSelectedRoomTypeChange("living");
                    else if (val === 24) onSelectedRoomTypeChange("bathroom");
                    else onSelectedRoomTypeChange(null);
                  }}
                />
              </div>
            )}

            {showRoomTypeControl && (
              <div data-testid="room-type-selector">
                <RoomTypeSelector
                  value={selectedRoomType}
                  onChange={onSelectedRoomTypeChange}
                  onTemperatureChange={onTargetTemperatureChange}
                  disabled={isLocked('selectedRoomType') || isLocked('desiredTemperature')}
                />
              </div>
            )}
          </Space>
        </Space>}

        <Space size="large" align="start" data-testid="control-panel-system-controls">
          {showSupplySection && <Space direction="vertical" align="start" data-testid="supply-settings-group">
            <small className="default-title">{t('heatPumpSimulation.supply.title')}</small>
            {showSupplyTemperatureControl && <div data-testid="supply-temp-control" className="supply-temp-slider">
              <NumericInput
                min={20}
                max={100}
                defaultValue={35}
                value={supplyTemperature}
                style={{ width: 160 }}
                suffix="°C"
                disabled={isLocked('supplyTemperature')}
                onChange={onSupplyTemperatureChange}
              />
            </div>}
            {(showAutoWeatherToggle || showHeatingCurveButton) && (
              <div data-testid="heating-curve-controls">
                <AutoAdjustSupplyButton
                    outdoorTemperature={outsideTemperature}
                    targetTemperature={targetTemperature}
                    currentRoomTemperature={currentRoomTemperature}
                    uValue={uValue}
                    totalArea={totalArea}
                    roofArea={roofArea}
                    wallArea={wallArea}
                    airChangeRate={airChangeRate}
                    radiatorCount={radiatorCount}
                    radiatorType={radiatorType}
                    valvePosition={valvePosition}
                    systemPressure={systemPressure}
                    onSupplyTemperatureChange={onSupplyTemperatureChange}
                    onShowHeatingCurve={onShowHeatingCurveClick}
                    autoWeatherCompensation={autoWeatherCompensation}
                    onAutoWeatherCompensationChange={onAutoWeatherCompensationChange}
                    showAutoToggle={showAutoWeatherToggle}
                    showHeatingCurveButton={showHeatingCurveButton}
                    autoToggleDisabled={false}
                />
              </div>
            )}
          </Space>}

          {showSystemPressureSection && <Space direction="vertical" align="start" data-testid="system-pressure-control">
            <small className="default-title">{t('heatPumpSimulation.supply.systemPressure')}</small>
            <NumericInput
              min={0.01}
              max={100}
              step={0.01}
              defaultValue={0.1}
              value={systemPressure}
              style={{ width: 195 }}
              suffix="bar"
              disabled={isLocked('systemPressure')}
              onChange={onSystemPressureChange}
            />
          </Space>}
        </Space>
      </Space>}
    </Space>
  );
};

ControlPanel.propTypes = {
  outsideTemperature: PropTypes.number.isRequired,
  onOutsideTemperatureChange: PropTypes.func.isRequired,
  targetTemperature: PropTypes.number.isRequired,
  onTargetTemperatureChange: PropTypes.func.isRequired,
  supplyTemperature: PropTypes.number.isRequired,
  onSupplyTemperatureChange: PropTypes.func.isRequired,
  uValue: PropTypes.number.isRequired,
  onUValueChange: PropTypes.func.isRequired,
  totalArea: PropTypes.number.isRequired,
  onTotalAreaChange: PropTypes.func.isRequired,
  rValue: PropTypes.number,
  roofArea: PropTypes.number.isRequired,
  wallArea: PropTypes.number.isRequired,
  envelopeLoss: PropTypes.number.isRequired,
  ventilationLoss: PropTypes.number.isRequired,
  radiatorType: PropTypes.string.isRequired,
  onRadiatorTypeChange: PropTypes.func.isRequired,
  radiatorTypeLocked: PropTypes.bool,
  radiatorSize: PropTypes.number.isRequired,
  onRadiatorSizeChange: PropTypes.func.isRequired,
  radiatorSizeLocked: PropTypes.bool,
  nominalPower: PropTypes.number.isRequired,
  qrad: PropTypes.number.isRequired,
  valvePosition: PropTypes.number.isRequired,
  thermostatOpening: PropTypes.number.isRequired,
  systemPressure: PropTypes.number.isRequired,
  onSystemPressureChange: PropTypes.func.isRequired,
  airChangeRate: PropTypes.number.isRequired,
  onAirChangeRateChange: PropTypes.func.isRequired,
  onShowUParametersClick: PropTypes.func.isRequired,
  onShowRadiatorTypesClick: PropTypes.func.isRequired,
  onShowBuildingEraClick: PropTypes.func.isRequired,
  onShowMapLocationClick: PropTypes.func.isRequired,
  onShowHeatingCurveClick: PropTypes.func.isRequired,
  autoWeatherCompensation: PropTypes.bool.isRequired,
  onAutoWeatherCompensationChange: PropTypes.func.isRequired,
  currentRoomTemperature: PropTypes.number,
  selectedCity: PropTypes.string.isRequired,
  onSelectedCityChange: PropTypes.func.isRequired,
  selectedRoomType: PropTypes.string,
  onSelectedRoomTypeChange: PropTypes.func.isRequired,
  profileControlState: PropTypes.shape({
    isLocked: PropTypes.func,
    isHidden: PropTypes.func,
  }),
};

export default ControlPanel;